/**
 * Hook to detect when the browser tab is visible or hidden
 * 
 * Uses the Page Visibility API to pause non-critical work when tab is in background.
 * This reduces CPU and memory usage when the user is not actively viewing the page.
 * 
 * @returns boolean - true if tab is visible, false if hidden
 * 
 * @example
 * ```tsx
 * const isVisible = usePageVisibility();
 * 
 * useEffect(() => {
 *   if (!isVisible) return; // Pause polling when tab is hidden
 *   const interval = setInterval(() => {
 *     // Do work
 *   }, 1000);
 *   return () => clearInterval(interval);
 * }, [isVisible]);
 * ```
 */
import { useState, useEffect } from 'react';
import { perfLog } from '../utils/performance';

export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState(() => {
    // Check if document is available (SSR safety)
    if (typeof document === 'undefined') return true;
    return !document.hidden;
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
      // CRITICAL FIX: Use lightweight logger to reduce [Violation] warnings
      perfLog.debug('[PageVisibility] Tab is now', document.hidden ? 'hidden' : 'visible');
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}

